import { library } from '@fortawesome/fontawesome-svg-core'
import {
    faAddressCard,
    faArrowPointer,
    faArrowRotateLeft,
    faArrowsUpDownLeftRight,
    faBagShopping,
    faBan,
    faCaretDown,
    faCartShopping,
    faCheck,
    faCheckSquare,
    faChevronDown,
    faChevronLeft,
    faCircleCheck,
    faCircleInfo,
    faCloudArrowUp,
    faCoffee,
    faEarthAmericas,
    faEnvelope,
    faEnvelopeOpenText,
    faEye,
    faFileArrowDown,
    faGear,
    faHeart,
    faKey,
    faList,
    faMoneyBills,
    faNoteSticky,
    faPaperPlane,
    faPenToSquare,
    faPlus,
    faRightFromBracket,
    faRightLong,
    faRing,
    faRuler,
    faScaleBalanced,
    faTrash,
    faTruck,
    faUpDownLeftRight,
    faUpload,
    faUser,
    faWallet,
    faXmark,
    faEnvelopesBulk,
    faCircleExclamation,
} from '@fortawesome/free-solid-svg-icons'
import { Configuration, UserDTO } from '@ringo/shared'
import { QueryClient, QueryClientProvider } from '@tanstack/react-query'
import { Route, BrowserRouter as Router, Routes } from 'react-router-dom'
import { ToastContainer } from 'react-toastify'
import 'react-toastify/dist/ReactToastify.css'
import { ClientApiProvider } from './common/api-context'
import { AuthContextProvider } from './common/hooks/auth/AuthContext'
import { LoaderProvider } from './components/loader/LoaderProvider'
import './index.css'
import { useCallback } from 'react'
import { ErrorBoundary } from './components/common/error-boundary'
import React from 'react'
import ProtectedRoute from './components/common/protected-route'
import NewsletterSuccess from './components/newsletter/newsletter-success'
import NewsletterError from './components/newsletter/newsletter-error'
import ConfirmAddress from './pages/auth/confirm-address'
import Login from './pages/auth/login'
import Register from './pages/auth/register'
import ForgotPassword from './pages/auth/forgot-password'
import { Init } from './pages/auth/init'
import ConfirmPasswordChange from './pages/auth/confirm-password-change'
import ConfirmEmail from './pages/auth/confirm-email'
import RegisterSuccess from './pages/auth/register-success'
import Authentications from './pages/my-account/authentications/authentications'
import AccountConfigurator from './pages/my-account/account-configurator'
import Settings from './pages/my-account/settings'
import MetalAccount from './pages/my-account/metal-account'
import Addresses from './pages/my-account/addresses/addresses'
import Details from './pages/my-account/details/details'
import MyAccount from './pages/my-account/overview'
import lazyWithPreload from './common/LazyWithPreload'
import { yupLocale } from './common/yup-locale'
import * as yup from 'yup'
import { GlobalContextProvider } from './common/global-context'
import { MaintenanceGuard } from './components/common/maintenance-guard'
import { LoginOldLink } from './pages/login-old-link'
import { Impersonate } from './pages/auth/impersonate'
import useVersionCheck from './common/hooks/useVersionCheck'
import { useTranslation } from './useTranslation'
import Button from './components/common/button'

library.add(
    faCheckSquare,
    faCoffee,
    faUser,
    faPaperPlane,
    faWallet,
    faTruck,
    faRing,
    faRuler,
    faArrowsUpDownLeftRight,
    faScaleBalanced,
    faEarthAmericas,
    faEnvelope,
    faKey,
    faHeart,
    faGear,
    faCheck,
    faRightFromBracket,
    faAddressCard,
    faCaretDown,
    faCircleCheck,
    faChevronDown,
    faXmark,
    faPlus,
    faCircleInfo,
    faTrash,
    faCartShopping,
    faUpload,
    faFileArrowDown,
    faMoneyBills,
    faChevronLeft,
    faRightLong,
    faEnvelopeOpenText,
    faCloudArrowUp,
    faEye,
    faCircleCheck,
    faList,
    faNoteSticky,
    faBagShopping,
    faPenToSquare,
    faArrowPointer,
    faArrowRotateLeft,
    faUpDownLeftRight,
    faBan,
    faEnvelopesBulk,
    faCircleExclamation
)
const queryClient = new QueryClient()
const apiConfig = new Configuration({ basePath: import.meta.env.VITE_API_PATH })
const Configurator = lazyWithPreload(() => import('./pages/configurator'))
const ShoppingCart = lazyWithPreload(() => import('./pages/shopping-cart/shopping-cart'))
const TableInput = lazyWithPreload(() => import('./pages/table-input'))
const AlloyInfo = lazyWithPreload(() => import('./pages/alloyInfo'))
const Orders = lazyWithPreload(() => import('./pages/my-account/orders/orders'))
const ConfiguratorContextProvider = lazyWithPreload(() => import('./pages/configurator.context'))
const Homepage = lazyWithPreload(() => import('./components/homepage'))
const FavouriteProfiles = lazyWithPreload(() => import('./pages/favourites/favourites'))

Configurator.preload()
ShoppingCart.preload()
TableInput.preload()
AlloyInfo.preload()
Orders.preload()
ConfiguratorContextProvider.preload()
Homepage.preload()
FavouriteProfiles.preload()

yup.setLocale(yupLocale)

// https://vitejs.dev/guide/build#load-error-handling
window.addEventListener('vite:preloadError', () => {
    window.location.reload() // for example, refresh the page
})

function App() {
    const isUserVerifiedPermissionChecker = useCallback((user: UserDTO) => user.verificationStatus === 'VERIFIED', [])
    const { t } = useTranslation()
    const { showMismatchDialog, ignore } = useVersionCheck()

    return (
        <ErrorBoundary>
            <React.Suspense>
                <QueryClientProvider client={queryClient}>
                    {/* eslint-disable-next-line @typescript-eslint/no-empty-function */}
                    <ClientApiProvider configuration={apiConfig}>
                        <AuthContextProvider>
                            {showMismatchDialog ? (
                                <div className="fixed z-50 bg-[#ffffffcc] h-full w-full flex items-center justify-center">
                                    <div className="p-8 rounded-md bg-white flex flex-col gap-5 items-center">
                                        <div>{t('newVersionAvailable')}</div>
                                        <div className="flex gap-3">
                                            <Button icon="xmark" type="squareGray" text={t('close')} onClick={ignore} />
                                            <Button text={t('update')} type={'squareBlue'} onClick={() => location.reload()}></Button>
                                        </div>
                                    </div>
                                </div>
                            ) : null}

                            <GlobalContextProvider>
                                <LoaderProvider>
                                    <Router>
                                        <Routes>
                                            <Route path="/" element={<Homepage />} />

                                            <Route element={<MaintenanceGuard />}>
                                                <Route element={<ConfiguratorContextProvider />}>
                                                    <Route path="/configurator" element={<Configurator />} />

                                                    <Route
                                                        path="/shopping-cart"
                                                        element={
                                                            <ProtectedRoute permissionChecker={isUserVerifiedPermissionChecker}>
                                                                <ShoppingCart />
                                                            </ProtectedRoute>
                                                        }
                                                    />
                                                    <Route
                                                        path="/favourites"
                                                        element={
                                                            <ProtectedRoute>
                                                                <FavouriteProfiles />
                                                            </ProtectedRoute>
                                                        }
                                                    />
                                                    <Route
                                                        path="/table-input"
                                                        element={
                                                            <ProtectedRoute permissionChecker={isUserVerifiedPermissionChecker}>
                                                                <TableInput />
                                                            </ProtectedRoute>
                                                        }
                                                    />
                                                    <Route
                                                        path="/account/orders"
                                                        element={
                                                            <ProtectedRoute>
                                                                <Orders />
                                                            </ProtectedRoute>
                                                        }
                                                    />
                                                </Route>

                                                <Route path="/alloy-info" element={<AlloyInfo />} />

                                                {/* My account routes */}
                                                <Route
                                                    path="/account/overview"
                                                    element={
                                                        <ProtectedRoute>
                                                            <MyAccount />
                                                        </ProtectedRoute>
                                                    }
                                                />
                                                <Route
                                                    path="/account/details"
                                                    element={
                                                        <ProtectedRoute>
                                                            <Details />
                                                        </ProtectedRoute>
                                                    }
                                                />
                                                <Route
                                                    path="/account/addresses"
                                                    element={
                                                        <ProtectedRoute>
                                                            <Addresses />
                                                        </ProtectedRoute>
                                                    }
                                                />
                                                <Route
                                                    path="/account/metal-account"
                                                    element={
                                                        <ProtectedRoute permissionChecker={isUserVerifiedPermissionChecker}>
                                                            <MetalAccount />
                                                        </ProtectedRoute>
                                                    }
                                                />
                                                <Route
                                                    path="/account/orders"
                                                    element={
                                                        <ProtectedRoute permissionChecker={isUserVerifiedPermissionChecker}>
                                                            <Orders />
                                                        </ProtectedRoute>
                                                    }
                                                />
                                                <Route
                                                    path="/account/authentications"
                                                    element={
                                                        <ProtectedRoute>
                                                            <Authentications />
                                                        </ProtectedRoute>
                                                    }
                                                />
                                                <Route
                                                    path="/account/settings"
                                                    element={
                                                        <ProtectedRoute>
                                                            <Settings />
                                                        </ProtectedRoute>
                                                    }
                                                />
                                                <Route
                                                    path="/account/configurator"
                                                    element={
                                                        <ProtectedRoute>
                                                            <AccountConfigurator />
                                                        </ProtectedRoute>
                                                    }
                                                />
                                                <Route path="/auth/login" element={<Login />} />
                                                <Route path="/login" element={<LoginOldLink />} />
                                                <Route path="/auth/register" element={<Register />} />
                                                <Route path="/auth/forgot-password" element={<ForgotPassword />} />
                                                <Route path="/auth/init" element={<Init />} />
                                                <Route path="/auth/impersonate" element={<Impersonate />} />
                                                <Route path="/auth/confirm-password-change" element={<ConfirmPasswordChange />} />
                                                <Route path="/auth/confirm-email" element={<ConfirmEmail />} />
                                                <Route path="/auth/register-success" element={<RegisterSuccess />} />

                                                <Route
                                                    path="/auth/confirm-address"
                                                    element={
                                                        <ProtectedRoute>
                                                            <ConfirmAddress />
                                                        </ProtectedRoute>
                                                    }
                                                />
                                                <Route path="/newsletter/success" element={<NewsletterSuccess />} />
                                                <Route path="/newsletter/error" element={<NewsletterError />} />
                                            </Route>
                                        </Routes>
                                    </Router>
                                    <ToastContainer></ToastContainer>
                                </LoaderProvider>
                            </GlobalContextProvider>
                        </AuthContextProvider>
                    </ClientApiProvider>
                </QueryClientProvider>
            </React.Suspense>
        </ErrorBoundary>
    )
}
export default App
